import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PlayersHeader } from "@/components/general/Players/PlayersHeader";
import { PlayersList } from "@/components/general/Players/PlayersList";
import { PlayersSort, SortOption, SortDirection } from "@/components/general/Players/PlayersSort";
import { playersListQueryOptions } from "@/api/playerList";
import { useSelectedTournament } from "@/context/tournamentContext";
import { useSelectedTeam } from "@/context/teamContext";

export default function PlayersPage() {
    const { tournamentId: globalTournamentId, setTournamentId: setGlobalTournamentId } = useSelectedTournament();
    const { teamId } = useSelectedTeam();
    const [localTournamentId, setLocalTournamentId] = useState<number | undefined>(undefined);
    const [isUsingLatest, setIsUsingLatest] = useState(globalTournamentId === undefined);

    const [sortOption, setSortOption] = useState<SortOption>("averageKPI");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

    const selectedTournamentId = localTournamentId !== undefined ? localTournamentId : globalTournamentId;
    const queryTournamentId = isUsingLatest && selectedTournamentId === undefined ? undefined : selectedTournamentId;

    const queryOpts = {
        ...playersListQueryOptions(queryTournamentId, teamId),
        enabled: true,
    };

    const { data: playerList, isLoading: playersLoading } = useQuery(queryOpts);

    useEffect(() => {
        if (playerList && isUsingLatest && !selectedTournamentId && playerList.tournamentId) {
            setLocalTournamentId(playerList.tournamentId);
            if (globalTournamentId === undefined) {
                setGlobalTournamentId(playerList.tournamentId);
            }
        }
    }, [playerList, selectedTournamentId, isUsingLatest, globalTournamentId, setGlobalTournamentId]);

    const handleTournamentChange = (id: number | undefined) => {
        setLocalTournamentId(id);
        setGlobalTournamentId(id);
        setIsUsingLatest(false);
    };

    const handleSortChange = (newSortOption: SortOption, newDirection: SortDirection) => {
        setSortOption(newSortOption);
        setSortDirection(newDirection);
    };

    const fieldPlayers = playerList?.kpis || [];
    const goalkeepers = playerList?.goalkeepersKpis || [];

    return (
        <main data-players-loaded={!playersLoading ? "true" : undefined} className="flex flex-col p-6">
            <PlayersHeader
                selectedTournamentId={selectedTournamentId}
                fallbackTournamentId={playerList?.tournamentId}
                onTournamentChange={handleTournamentChange}
                isLoading={playersLoading}
            />
            <PlayersSort onSortChange={handleSortChange} />
            <PlayersList
                title="Goalkeepers"
                sortOption={sortOption}
                sortDirection={sortDirection}
                players={goalkeepers}
                isLoading={playersLoading && goalkeepers.length === 0}
                teamId={teamId}
            />
            <PlayersList
                title="Field Players"
                sortOption={sortOption}
                sortDirection={sortDirection}
                players={fieldPlayers}
                isLoading={playersLoading}
                teamId={teamId}
            />
            <div className="lg:hidden py-6" />
        </main>
    );
}
